/**
 * previewGenerator.js
 * Generates actual HTML from template sections + sample data (no EJS).
 * Used by PrintPreview to render a realistic invoice preview.
 */

import { getPageSize } from "./elements";
import { generateSampleData } from "./sampleData";
import { resolveRichText } from "./richTextHelpers";
import { slotBorderCss } from "./borderHelpers";

// ─── helpers ────────────────────────────────────────────────────────────────

function escapeHtml(str) {
  return String(str == null ? "" : str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function spacingStyle(p) {
  const parts = [];
  if (p.marginTop) parts.push(`margin-top: ${p.marginTop}px`);
  if (p.marginBottom) parts.push(`margin-bottom: ${p.marginBottom}px`);
  if (p.paddingTop) parts.push(`padding-top: ${p.paddingTop}px`);
  if (p.paddingBottom) parts.push(`padding-bottom: ${p.paddingBottom}px`);
  if (p.paddingLeft) parts.push(`padding-left: ${p.paddingLeft}px`);
  if (p.paddingRight) parts.push(`padding-right: ${p.paddingRight}px`);
  if (p.borderWidth > 0) {
    parts.push(`border: ${p.borderWidth}px ${p.borderStyle || "solid"} ${p.borderColor || "#000000"}`);
    if (p.borderRadius > 0) parts.push(`border-radius: ${p.borderRadius}px`);
  }
  // Inner element border (per-side, independent of global border)
  const eb = p.elementBorder;
  if (eb && (eb.top || eb.bottom || eb.left || eb.right)) {
    const s = eb.style || "solid";
    const c = eb.color || "#000000";
    if (eb.top)    parts.push(`border-top: ${eb.top}px ${s} ${c}`);
    if (eb.bottom) parts.push(`border-bottom: ${eb.bottom}px ${s} ${c}`);
    if (eb.left)   parts.push(`border-left: ${eb.left}px ${s} ${c}`);
    if (eb.right)  parts.push(`border-right: ${eb.right}px ${s} ${c}`);
    parts.push(`box-sizing: border-box !important`);
  }
  return parts.join("; ");
}

function buildCellBorderCss({ col, borderColor, allCellBorders, includeBottom = true }) {
  const w = "1px";
  const parts = [];
  if (col?.borderLeft || allCellBorders)  parts.push(`border-left:${w} solid ${borderColor}`);
  if (col?.borderRight || allCellBorders) parts.push(`border-right:${w} solid ${borderColor}`);
  if (allCellBorders) {
    parts.push(`border-top:${w} solid ${borderColor}`);
    parts.push(`border-bottom:${w} solid ${borderColor}`);
  } else if (includeBottom) {
    parts.push(`border-bottom:${w} solid ${borderColor}`);
  }
  return parts.join(";");
}

function resolveValue(binding, staticValue, data) {
  if (binding) {
    const val = binding.split(".").reduce((o, k) => o?.[k], data);
    if (val != null) return String(val);
  }
  return staticValue || "";
}

function buildTableHeaderHTML(cols, groups, p) {
  const allBorders = p.allCellBorders;
  if (!groups || groups.length === 0) {
    const cells = cols
      .map(
        (c) =>
          `<th style="background-color:${p.headerBgColor};color:${p.headerTextColor};font-weight:${p.headerFontWeight};padding:${p.cellPadding}px;text-align:${c.align||"left"};width:${c.width};${buildCellBorderCss({col:c,borderColor:p.borderColor,allCellBorders:allBorders})};">${escapeHtml(c.header)}</th>`
      )
      .join("");
    return `<tr>${cells}</tr>`;
  }

  const coverage = new Array(cols.length).fill(null);
  groups.forEach((g) => {
    for (let i = g.startCol; i < g.startCol + g.span && i < cols.length; i++) coverage[i] = g;
  });

  const row1 = cols
    .map((col, i) => {
      const cb = buildCellBorderCss({col, borderColor: p.borderColor, allCellBorders: allBorders});
      const g = groups.find((g2) => g2.startCol === i);
      if (g)
        return `<th colspan="${g.span}" style="background-color:${p.headerBgColor};color:${p.headerTextColor};font-weight:${p.headerFontWeight};padding:${Math.floor(p.cellPadding/2)}px ${p.cellPadding}px;text-align:center;${cb};">${escapeHtml(g.label)}</th>`;
      const inGroup = coverage[i] !== null && groups.some((g2) => g2.startCol !== i && i >= g2.startCol && i < g2.startCol + g2.span);
      if (inGroup) return null;
      return `<th rowspan="2" style="background-color:${p.headerBgColor};color:${p.headerTextColor};font-weight:${p.headerFontWeight};padding:${p.cellPadding}px;text-align:${col.align||"left"};width:${col.width};vertical-align:middle;${cb};">${escapeHtml(col.header)}</th>`;
    })
    .filter(Boolean)
    .join("");

  const row2 = cols
    .map((col, i) => coverage[i] !== null
      ? `<th style="background-color:${p.headerBgColor};color:${p.headerTextColor};font-weight:${p.headerFontWeight};padding:${Math.floor(p.cellPadding/2)}px ${p.cellPadding}px;text-align:${col.align||"left"};width:${col.width};${buildCellBorderCss({col, borderColor: p.borderColor, allCellBorders: allBorders})};">${escapeHtml(col.header)}</th>`
      : null
    )
    .filter(Boolean)
    .join("");

  return `<tr>${row1}</tr><tr>${row2}</tr>`;
}

function buildTotalRowHTML(cols, items, p) {
  if (!p.showTotalRow) return "";
  const allBorders = p.allCellBorders;
  const sums = {};
  const units = {};
  cols.forEach((c) => {
    if (c.showTotal) {
      sums[c.key] = 0;
      units[c.key] = "";
      items.forEach((it) => {
        const raw = String(it[c.key] ?? "").trim();
        if (!raw) return;
        const m = raw.match(/^([\d,.\s-]+)\s*([a-zA-Z\s/.]+)?$/);
        if (m) {
          sums[c.key] += parseFloat(m[1].replace(/[, ]/g, "")) || 0;
          if (m[2] && !units[c.key]) units[c.key] = m[2].trim();
        }
      });
    }
  });
  const cells = cols
    .map((c, i) => {
      const cb = (allBorders || c.borderLeft || c.borderRight)
        ? buildCellBorderCss({col: c, borderColor: p.borderColor, allCellBorders: allBorders, includeBottom: !!allBorders})
        : "";
      const style = `padding:${p.cellPadding}px;text-align:${c.align||"left"};border-top:2px solid ${p.borderColor};font-weight:bold;${cb ? cb + ";" : ""}`;
      if (i === 0) return `<td style="${style}">${escapeHtml(p.totalLabel == null ? "Total" : p.totalLabel)}</td>`;
      if (c.showTotal) {
        const formatted = sums[c.key].toLocaleString("en-IN", { minimumFractionDigits: 2 });
        const suffix = units[c.key] ? ` ${units[c.key]}` : "";
        return `<td style="${style}">${formatted}${suffix}</td>`;
      }
      return `<td style="${style}"></td>`;
    })
    .join("");
  return `<tfoot><tr>${cells}</tr></tfoot>`;
}

// ─── per-element renderers ────────────────────────────────────────────────────

function renderLineItemsSlice(p, items) {
  const cols = p.columns || [];
  const groups = p.columnGroups || [];
  const sp = spacingStyle(p);
  const allBorders = p.allCellBorders;

  const headerHTML = buildTableHeaderHTML(cols, groups, p);
  const hasSubRows = (p.subRows || []).length > 0;

  const bodyRows = items
    .map((item, i) => {
      const stripBg = p.stripedRows && i % 2 === 1 ? p.stripedColor : "transparent";
      const mainCells = cols
        .map((c) => {
          const val = c.key === "index" ? item.index || i + 1 : item[c.key] || "";
          const cb = buildCellBorderCss({col: c, borderColor: p.borderColor, allCellBorders: allBorders, includeBottom: !hasSubRows});
          return `<td style="padding:${p.cellPadding}px;text-align:${c.align||"left"};${cb};background-color:${stripBg};">${escapeHtml(val)}</td>`;
        })
        .join("");
      const mainRow = `<tr>${mainCells}</tr>`;

      const subRowHtml = (p.subRows || [])
        .map((sr, srIdx) => {
          const totalCols = cols.length;
          const startCol = Math.min(sr.startCol || 0, totalCols - 1);
          const colSpan = Math.min(sr.colSpan || totalCols, totalCols - startCol);
          const afterCols = totalCols - startCol - colSpan;
          const isLast = srIdx === (p.subRows || []).length - 1;
          const border = isLast ? `border-bottom:1px solid ${p.borderColor};` : "";
          const cellStyle = `padding:${Math.floor(p.cellPadding/2)}px ${p.cellPadding}px;font-size:${sr.fontSize||10}px;color:${sr.color||"#000000"};font-style:${sr.fontStyle||"italic"};text-align:${sr.align||"left"};${border}background-color:${stripBg};`;
          const val = item[sr.key] || "";
          const content = val ? (sr.label ? `${sr.label}: ${val}` : val) : "";
          return [
            startCol > 0 ? `<td colspan="${startCol}" style="padding:${Math.floor(p.cellPadding/2)}px ${p.cellPadding}px;${border}background-color:${stripBg};"></td>` : "",
            `<td colspan="${colSpan}" style="${cellStyle}">${escapeHtml(content)}</td>`,
            afterCols > 0 ? `<td colspan="${afterCols}" style="padding:${Math.floor(p.cellPadding/2)}px ${p.cellPadding}px;${border}background-color:${stripBg};"></td>` : "",
          ]
            .filter(Boolean)
            .join("");
        })
        .map((cells) => `<tr>${cells}</tr>`)
        .join("");

      return mainRow + subRowHtml;
    })
    .join("");

  const totalFooter = buildTotalRowHTML(cols, items, p);

  return `<table style="width:100%;table-layout:fixed;font-size:${p.fontSize}px;font-family:${p.fontFamily};border-collapse:collapse;${sp ? sp + ";" : ""}"><thead>${headerHTML}</thead><tbody>${bodyRows}</tbody>${totalFooter}</table>`;
}

function renderSummaryTable(p, data) {
  const cols = p.columns || [];
  const groups = p.columnGroups || [];
  const rawItems = data[p.loopVariable || "gstSummary"];
  // Defensive: sample data may include the key as a non-array value (e.g. an object).
  // Without this guard, .map throws.
  const items = Array.isArray(rawItems) ? rawItems : (Array.isArray(data.gstSummary) ? data.gstSummary : []);
  const sp = spacingStyle(p);
  const allBorders = p.allCellBorders;

  const headerHTML = buildTableHeaderHTML(cols, groups, p);

  const bodyRows = items
    .map((item, i) => {
      const stripBg = p.stripedRows && i % 2 === 1 ? p.stripedColor : "transparent";
      const cells = cols
        .map((c) => {
          const cb = buildCellBorderCss({col: c, borderColor: p.borderColor, allCellBorders: allBorders});
          return `<td style="padding:${p.cellPadding}px;text-align:${c.align||"left"};${cb};background-color:${stripBg};">${escapeHtml(item[c.key] || "")}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const totalFooter = buildTotalRowHTML(cols, items, p);

  return `<table style="width:100%;table-layout:fixed;font-size:${p.fontSize}px;font-family:${p.fontFamily};border-collapse:collapse;${sp ? sp + ";" : ""}"><thead>${headerHTML}</thead><tbody>${bodyRows}</tbody>${totalFooter}</table>`;
}

function elementToHTML(el, data) {
  if (!el || !el.type) return "";
  const { type, props: p } = el;
  const sp = spacingStyle(p);
  const outer = (inner) => `<div style="${sp}">${inner}</div>`;

  switch (type) {
    case "heading": {
      const txt = resolveValue(p.ejsBinding, p.content, data);
      return `<div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};color:${p.color};text-align:${p.textAlign};font-weight:${p.fontWeight};font-style:${p.fontStyle};text-transform:${p.textTransform||"none"};letter-spacing:${p.letterSpacing||0}px;${sp}">${escapeHtml(txt)}</div>`;
    }
    case "text": {
      if (p.richText) {
        // Resolve EJS-variable placeholders against `data` for live preview.
        // Supports both legacy span markup and current `{{path}}` format.
        const html = resolveRichText(p.richContent || "", data, escapeHtml);
        return `<div class="text-block-container" data-component-type="text_block" style="page-break-inside:avoid !important;break-inside:avoid !important;box-sizing:border-box;${sp}">${html}</div>`;
      }
      const txt = resolveValue(p.ejsBinding, p.content, data);
      return `<div class="text-block-container" data-component-type="text_block" style="font-size:${p.fontSize}px;font-family:${p.fontFamily};color:${p.color};text-align:${p.textAlign};font-weight:${p.fontWeight};font-style:${p.fontStyle};line-height:${p.lineHeight};white-space:pre-wrap;page-break-inside:avoid !important;break-inside:avoid !important;box-sizing:border-box;${sp}">${escapeHtml(txt)}</div>`;
    }
    case "footer": {
      const txt = resolveValue(p.ejsBinding, p.content, data);
      return `<div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};color:${p.color};text-align:${p.textAlign};font-style:${p.fontStyle};${sp}">${escapeHtml(txt)}</div>`;
    }
    case "companyLogo": {
      const src = resolveValue(p.ejsBinding, p.src, data) || p.src || "";
      return `<div style="${sp}"><img src="${escapeHtml(src)}" style="height:${p.height}px;max-width:100%;object-fit:contain;" /></div>`;
    }
    case "image": {
      const src = resolveValue(p.ejsBinding, p.src, data) || p.src || "";
      return `<div style="${sp}"><img src="${escapeHtml(src)}" style="width:${p.width||"100%"};max-height:${p.maxHeight||200}px;object-fit:contain;" /></div>`;
    }
    case "invoiceDetails": {
      const align = p.alignment || "right";
      const rows = (p.fields || [])
        .map((f) => {
          const val = resolveValue(f.ejsBinding, f.value, data);
          return `<tr><td style="color:${p.labelColor};padding:2px 12px 2px 0;text-align:${p.labelAlign||"left"};width:${p.labelWidth}px;">${escapeHtml(f.label)}:</td><td style="color:${p.valueColor};font-weight:500;padding:2px 0;text-align:${p.valueAlign||"right"};">${escapeHtml(val)}</td></tr>`;
        })
        .join("");
      return `<div style="text-align:${align};${sp}"><table style="font-size:${p.fontSize}px;font-family:${p.fontFamily};border-collapse:collapse;${align==="right"?"margin-left:auto;":""}"><tbody>${rows}</tbody></table></div>`;
    }
    case "customerInfo": {
      const rows = (p.fields || [])
        .map((f) => {
          const val = resolveValue(f.ejsBinding, f.value, data);
          return `<div style="padding:2px 0;"><span style="color:${p.labelColor};font-size:${(p.fontSize||12)-1}px;">${escapeHtml(f.label)}: </span><span style="color:${p.valueColor};">${escapeHtml(val)}</span></div>`;
        })
        .join("");
      return `<div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};${sp}">${rows}</div>`;
    }
    case "keyValueList": {
      const vGap = p.verticalGap == null ? 4 : p.verticalGap;
      const headingHtml = p.componentHeading
        ? `<div style="font-weight:bold;font-size:${(p.fontSize||11)+1}px;color:${p.color};margin-bottom:4px;width:100%;">${escapeHtml(p.componentHeading)}</div>`
        : "";
      const rowMargin = `margin-top:${vGap}px;margin-bottom:${vGap}px;`;

      const resolveSingleVal = (binding, defaultText) => {
        if (binding) return escapeHtml(resolveValue(binding, "", data));
        return escapeHtml(defaultText || "");
      };

      const rows = (p.items || []).map((it) => {
        const layoutPattern = it.layoutPattern || "single";
        if (layoutPattern === "dual") {
          const lv = resolveSingleVal(it.left?.ejsBinding, it.left?.defaultValue);
          const rv = resolveSingleVal(it.right?.ejsBinding, it.right?.defaultValue);
          return `<div style="display:flex;width:100%;${rowMargin}box-sizing:border-box;"><div style="width:50%;display:flex;align-items:center;box-sizing:border-box;padding-right:8px;"><div style="width:${p.labelWidth||"40%"};text-align:${p.labelAlign||"left"};font-weight:600 !important;color:${p.labelColor};">${escapeHtml(it.left?.label||"")}</div><div style="flex:1;text-align:${p.valueAlign||"left"};color:${p.valueColor};word-break:break-word;">${lv}</div></div><div style="width:50%;display:flex;align-items:center;box-sizing:border-box;padding-left:8px;"><div style="width:${p.labelWidth||"40%"};text-align:${p.labelAlign||"left"};font-weight:600 !important;color:${p.labelColor};">${escapeHtml(it.right?.label||"")}</div><div style="flex:1;text-align:${p.valueAlign||"left"};color:${p.valueColor};word-break:break-word;">${rv}</div></div></div>`;
        }
        const rowType = it.rowType || "label_value";
        if (rowType === "value_only") {
          const rowFs = it.rowFontSize ? `font-size:${it.rowFontSize}px;` : "";
          const rowBold = it.rowIsBold ? "font-weight:bold;" : "";
          const val = it.ejsBinding ? resolveValue(it.ejsBinding, "", data) : (it.staticText || "");
          return `<p style="${rowFs}${rowBold}color:${p.valueColor||p.color};${rowMargin}text-align:${p.valueAlign||"left"};width:100%;word-break:break-word;box-sizing:border-box;">${escapeHtml(val)}</p>`;
        }
        const val = resolveSingleVal(it.ejsBinding, it.defaultValue);
        return `<div style="display:flex;gap:${p.gap||4}px;${rowMargin}line-height:1.4;width:100%;box-sizing:border-box;"><div style="width:${p.labelWidth||"40%"};color:${p.labelColor};text-align:${p.labelAlign||"left"};font-weight:600 !important;word-break:break-word;">${escapeHtml(it.label||"")}</div><div style="flex:1;color:${p.valueColor};text-align:${p.valueAlign||"left"};word-break:break-word;">${val}</div></div>`;
      }).join("");
      return `<div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};color:${p.color};width:100%;box-sizing:border-box;${sp}">${headingHtml}${rows}</div>`;
    }
    case "taxDetails": {
      const divider = `border-top:${p.sectionDividerWidth||1}px solid ${p.sectionDividerColor||"#000000"};margin:2px 0;`;
      const buildRow = (it) => {
        const val = it.ejsBinding ? resolveValue(it.ejsBinding, "", data) : "";
        const fw = it.isBold ? "font-weight:bold;" : "";
        return `<div style="display:flex;gap:${p.gap||4}px;padding:${p.rowPadding||4}px 0;line-height:1.4;width:100%;box-sizing:border-box;${fw}"><div style="width:${p.labelWidth||"60%"};color:${p.labelColor};text-align:${p.labelAlign||"left"};word-break:break-word;">${escapeHtml(it.label||"")}</div><div style="flex:1;color:${p.valueColor};text-align:${p.valueAlign||"right"};word-break:break-word;">${escapeHtml(val)}</div></div>`;
      };
      const preHtml = (p.preLoopItems || []).map(buildRow).join("");
      const postHtml = (p.postLoopItems || []).map(buildRow).join("");
      // tax_detail is treated as a key→value object in live preview, matching
      // the runtime EJS template loop. Falls back to array iteration only if
      // the sample data is explicitly an array (back-compat with older mocks).
      const td = data[p.loopVariable];
      let loopHtml = "";
      if (td && typeof td === "object" && !Array.isArray(td)) {
        loopHtml = Object.entries(td).map(([k, v]) =>
          `<div style="display:flex;gap:${p.gap||4}px;padding:${p.rowPadding||4}px 0;line-height:1.4;width:100%;box-sizing:border-box;"><div style="width:${p.labelWidth||"60%"};color:${p.labelColor};text-align:${p.labelAlign||"left"};word-break:break-word;">${escapeHtml(k)}</div><div style="flex:1;color:${p.valueColor};text-align:${p.valueAlign||"right"};word-break:break-word;">${escapeHtml(String(v))}</div></div>`
        ).join("");
      } else if (Array.isArray(td)) {
        loopHtml = td.map((row) =>
          `<div style="display:flex;gap:${p.gap||4}px;padding:${p.rowPadding||4}px 0;line-height:1.4;width:100%;box-sizing:border-box;"><div style="width:${p.labelWidth||"60%"};color:${p.labelColor};text-align:${p.labelAlign||"left"};word-break:break-word;">${escapeHtml(row[p.loopRowLabelKey]||"")}</div><div style="flex:1;color:${p.valueColor};text-align:${p.valueAlign||"right"};word-break:break-word;">${escapeHtml(row[p.loopRowValueKey]||"")}</div></div>`
        ).join("");
      }
      return `<div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};color:${p.color};width:100%;box-sizing:border-box;${sp}">${preHtml}${preHtml ? `<div style="${divider}"></div>` : ""}${loopHtml}${postHtml ? `<div style="${divider}"></div>` : ""}${postHtml}</div>`;
    }

    case "totals": {
      const align = p.alignment || "right";
      const rows = (p.rows || [])
        .map((r) => {
          const val = resolveValue(r.ejsBinding, r.value || "0.00", data);
          const fw = r.fontWeight === "bold" ? "font-weight:bold;" : "";
          return `<tr><td style="${fw}color:${p.labelColor};padding:4px 20px 4px 0;text-align:${p.labelAlign||"left"};">${escapeHtml(r.label)}:</td><td style="${fw}color:${p.valueColor};text-align:${p.valueAlign||"right"};padding:4px 0;">${escapeHtml(val)}</td></tr>`;
        })
        .join("");
      return `<div style="text-align:${align};${sp}"><table style="font-size:${p.fontSize}px;font-family:${p.fontFamily};border-collapse:collapse;${align==="right"?"margin-left:auto;":""}width:${p.width}px;"><tbody>${rows}</tbody></table></div>`;
    }
    case "lineItems": {
      const raw = data[p.loopVariable || "items"];
      const items = Array.isArray(raw) ? raw : (Array.isArray(data.items) ? data.items : []);
      return renderLineItemsSlice(p, items);
    }
    case "summaryTable": {
      return renderSummaryTable(p, data);
    }
    case "twoColumn": {
      const leftH = p.leftColumn?.type ? elementToHTML({ type: p.leftColumn.type, props: p.leftColumn.props || {} }, data) : "";
      const rightH = p.rightColumn?.type ? elementToHTML({ type: p.rightColumn.type, props: p.rightColumn.props || {} }, data) : "";
      const vAlign = { top: "flex-start", center: "center", bottom: "flex-end" }[p.verticalAlign] || "flex-start";
      const leftBorder = slotBorderCss(p.leftColumn?.elementBorder);
      const rightBorder = slotBorderCss(p.rightColumn?.elementBorder);
      return `<div class="two-columns-wrapper" data-component-type="two_columns" style="display:flex;gap:${p.gap}px;align-items:${vAlign};box-sizing:border-box !important;page-break-inside:avoid !important;break-inside:avoid !important;${sp}"><div style="width:${p.leftWidth};flex-shrink:0;box-sizing:border-box !important;${leftBorder}">${leftH}</div><div style="flex:1;box-sizing:border-box !important;${rightBorder}">${rightH}</div></div>`;
    }
    case "threeColumn": {
      const leftH = p.leftColumn?.type ? elementToHTML({ type: p.leftColumn.type, props: p.leftColumn.props || {} }, data) : "";
      const midH = p.midColumn?.type ? elementToHTML({ type: p.midColumn.type, props: p.midColumn.props || {} }, data) : "";
      const rightH = p.rightColumn?.type ? elementToHTML({ type: p.rightColumn.type, props: p.rightColumn.props || {} }, data) : "";
      const vAlign = { top: "flex-start", center: "center", bottom: "flex-end" }[p.verticalAlign] || "flex-start";
      const leftBorder = slotBorderCss(p.leftColumn?.elementBorder);
      const midBorder = slotBorderCss(p.midColumn?.elementBorder);
      const rightBorder = slotBorderCss(p.rightColumn?.elementBorder);
      return `<div class="three-columns-wrapper" data-component-type="three_columns" style="display:flex;gap:${p.gap}px;align-items:${vAlign};box-sizing:border-box !important;page-break-inside:avoid !important;break-inside:avoid !important;${sp}"><div style="width:${p.leftWidth};flex-shrink:0;box-sizing:border-box !important;${leftBorder}">${leftH}</div><div style="width:${p.midWidth};flex-shrink:0;box-sizing:border-box !important;${midBorder}">${midH}</div><div style="flex:1;box-sizing:border-box !important;${rightBorder}">${rightH}</div></div>`;
    }
    case "fourColumn": {
      const c1H = p.col1?.type ? elementToHTML({ type: p.col1.type, props: p.col1.props || {} }, data) : "";
      const c2H = p.col2?.type ? elementToHTML({ type: p.col2.type, props: p.col2.props || {} }, data) : "";
      const c3H = p.col3?.type ? elementToHTML({ type: p.col3.type, props: p.col3.props || {} }, data) : "";
      const c4H = p.col4?.type ? elementToHTML({ type: p.col4.type, props: p.col4.props || {} }, data) : "";
      const vAlign = { top: "flex-start", center: "center", bottom: "flex-end" }[p.verticalAlign] || "flex-start";
      const c1Border = slotBorderCss(p.col1?.elementBorder);
      const c2Border = slotBorderCss(p.col2?.elementBorder);
      const c3Border = slotBorderCss(p.col3?.elementBorder);
      const c4Border = slotBorderCss(p.col4?.elementBorder);
      return `<div class="four-columns-wrapper" data-component-type="four_columns" style="display:flex;gap:${p.gap}px;align-items:${vAlign};box-sizing:border-box !important;page-break-inside:avoid !important;break-inside:avoid !important;${sp}"><div style="width:${p.col1Width};flex-shrink:0;box-sizing:border-box !important;${c1Border}">${c1H}</div><div style="width:${p.col2Width};flex-shrink:0;box-sizing:border-box !important;${c2Border}">${c2H}</div><div style="width:${p.col3Width};flex-shrink:0;box-sizing:border-box !important;${c3Border}">${c3H}</div><div style="width:${p.col4Width||'25%'};flex-shrink:0;box-sizing:border-box !important;${c4Border}">${c4H}</div></div>`;
    }
    case "verticalLayout": {
      // Recursive stack — same logic as the EJS compiler but resolves
      // bindings to live sample data for the preview.
      const rows = p.rows || [];
      const rowsHtml = rows.map((row) => {
        const inner = row.childType
          ? elementToHTML({ type: row.childType, props: row.childProps || {} }, data)
          : "";
        const rowBorder = slotBorderCss(row.elementBorder);
        return `<div style="width:100%;box-sizing:border-box !important;${rowBorder}">${inner}</div>`;
      }).join("");
      return `<div style="display:flex;flex-direction:column;gap:${p.gap||0}px;width:100%;box-sizing:border-box;${sp}">${rowsHtml}</div>`;
    }
    case "divider": {
      return outer(`<hr style="border:none;border-top:${p.thickness||1}px ${p.style||"solid"} ${p.color||"#000000"};" />`);
    }
    case "spacer":
      return `<div style="height:${p.height||24}px;${sp}"></div>`;
    case "pageBreak":
      return `<div class="page-break-visual"></div>`;
    case "pageNumber": {
      const fmt = (p.format || "Page {current} of {total}").replace("{current}", data.currentPage || 1).replace("{total}", data.totalPages || 1);
      return `<div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};color:${p.color};text-align:${p.textAlign};${sp}">${fmt}</div>`;
    }
    case "signature": {
      return `<div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};${sp}"><div style="border-bottom:1px solid #9CA3AF;min-width:${p.lineWidth||120}px;height:${p.lineHeight||40}px;"></div><div style="margin-top:4px;color:${p.labelColor||"#000000"}">${escapeHtml(p.label||"Authorised Signatory")}</div></div>`;
    }
    case "paymentTerms": {
      const terms = resolveValue(p.ejsBinding, p.content, data);
      return `<div style="font-size:${p.fontSize}px;font-family:${p.fontFamily};color:${p.color};${sp}"><div style="font-weight:bold;margin-bottom:4px;">${escapeHtml(p.title||"Payment Terms")}</div><div>${escapeHtml(terms)}</div></div>`;
    }
    case "carriedForward": {
      const cols = (p.columns || []).map((c) => `<td style="padding:${p.cellPadding||4}px;text-align:${c.align||"right"};font-weight:bold;">${escapeHtml(c.label||c.key)}: —</td>`).join("");
      return `<div style="${sp}"><table style="width:100%;font-size:${p.fontSize||10}px;"><tbody><tr>${cols}</tr></tbody></table></div>`;
    }
    case "broughtForward": {
      const cols = (p.columns || []).map((c) => `<td style="padding:${p.cellPadding||4}px;text-align:${c.align||"right"};font-weight:bold;">${escapeHtml(c.label||c.key)}: —</td>`).join("");
      return `<div style="${sp}"><table style="width:100%;font-size:${p.fontSize||10}px;"><tbody><tr>${cols}</tr></tbody></table></div>`;
    }
    case "qrCode":
    case "barcode": {
      const size = p.size || 80;
      return outer(`<div style="width:${size}px;height:${size}px;background:#F3F4F6;border:1px dashed #D1D5DB;display:flex;align-items:center;justify-content:center;font-size:9px;color:#9CA3AF;">[${type === "qrCode" ? "QR" : "Barcode"}]</div>`);
    }
    case "custom": {
      const fields = (p.fields || [])
        .map((f) => `<div style="padding:1px 0;font-size:${p.fontSize||12}px;"><span style="color:#000000;font-size:${(p.fontSize||12)-1}px;">${escapeHtml(f.label||f.name)}: </span><span>${escapeHtml(f.value||f.defaultValue||"")}</span></div>`)
        .join("");
      return `<div style="${sp}">${fields}</div>`;
    }
    default:
      return "";
  }
}

// ─── main pagination logic ────────────────────────────────────────────────────

function computePageBreaks(lineItemsEl, data, pageSettings, sections) {
  const p = lineItemsEl.props;
  const raw = data[p.loopVariable || "items"];
  const items = Array.isArray(raw) ? raw : (Array.isArray(data.items) ? data.items : []);
  const linesPerItem = 1 + (p.subRows || []).length;

  // Estimate available lines per page (same logic as ejsGenerator)
  const pageHeightMM = getPageSize(pageSettings.pageSize, pageSettings).heightMM;
  const mTop = pageSettings.marginTop || 20;
  const mBottom = pageSettings.marginBottom || 20;
  const availMM = pageHeightMM - mTop - mBottom;

  const estimateH = (el) => {
    const ep = el.props;
    switch (el.type) {
      case "heading": return (ep.fontSize || 28) / 3 + 5;
      case "text": return (ep.fontSize || 12) / 3 + 3;
      case "companyLogo": return (ep.height || 60) / 3.78 + 3;
      case "invoiceDetails": return (ep.fields || []).length * 6 + 5;
      case "customerInfo": return (ep.fields || []).length * 5 + 8;
      case "twoColumn": case "threeColumn": case "fourColumn": return 20;
      case "divider": return 2;
      case "spacer": return (ep.height || 24) / 3.78;
      case "totals": return (ep.rows || []).length * 6 + 5;
      case "signature": return 15;
      case "pageNumber": case "carriedForward": case "broughtForward": return 6;
      default: return 8;
    }
  };

  const phMM = (sections.pageHeader || []).reduce((s, el) => s + estimateH(el), 0);
  const pfMM = (sections.pageFooter || []).reduce((s, el) => s + estimateH(el), 0);
  const bodyAvailMM = availMM - phMM - pfMM - 8 - 5;

  const rowPx = (p.cellPadding || 8) * 2 + (p.fontSize || 11) + 2;
  const rowMM = rowPx / 3.78;
  const maxLines = Math.max(5, Math.floor(bodyAvailMM / rowMM));

  const pageStarts = [0];
  let lineCount = 0;
  for (let k = 0; k < items.length; k++) {
    if (lineCount + linesPerItem > maxLines && lineCount > 0) {
      pageStarts.push(k);
      lineCount = 0;
    }
    lineCount += linesPerItem;
  }
  if (pageStarts[pageStarts.length - 1] !== items.length) pageStarts.push(items.length);
  return pageStarts;
}

function wrapSection(key, html, pageSettings) {
  const sp = pageSettings.sectionPadding?.[key] || { top: 0, bottom: 0 };
  const style = [sp.top > 0 ? `padding-top:${sp.top}px` : "", sp.bottom > 0 ? `padding-bottom:${sp.bottom}px` : ""].filter(Boolean).join(";");
  return style ? `<div style="${style}">${html}</div>` : html;
}

// ─── public API ──────────────────────────────────────────────────────────────

/**
 * Returns the number of items needed to approximately fill `pageCount` pages
 * for the given template's line items configuration.
 */
export function computeItemsForPages(sections, pageSettings, pageCount) {
  const ps = { marginTop: 20, marginRight: 20, marginBottom: 20, marginLeft: 20, fontFamily: "Arial, sans-serif", fontSize: 12, ...pageSettings };
  const lineItemsEl = (sections.body || []).find((el) => el.type === "lineItems");
  if (!lineItemsEl) return [7, 18, 30][pageCount - 1] || 7;

  const p = lineItemsEl.props;
  const linesPerItem = 1 + (p.subRows || []).length;

  const pageHeightMM = getPageSize(ps.pageSize, ps).heightMM;
  const availMM = pageHeightMM - (ps.marginTop || 20) - (ps.marginBottom || 20);

  const estimateH = (el) => {
    const ep = el.props;
    switch (el.type) {
      case "heading": return (ep.fontSize || 28) / 3 + 5;
      case "text": return (ep.fontSize || 12) / 3 + 3;
      case "invoiceDetails": return (ep.fields || []).length * 6 + 5;
      case "customerInfo": return (ep.fields || []).length * 5 + 8;
      case "twoColumn": case "threeColumn": case "fourColumn": return 20;
      case "divider": return 2;
      case "spacer": return (ep.height || 24) / 3.78;
      case "totals": return (ep.rows || []).length * 6 + 5;
      case "signature": return 15;
      case "pageNumber": case "carriedForward": case "broughtForward": return 6;
      default: return 8;
    }
  };

  const phMM = (sections.pageHeader || []).reduce((s, el) => s + estimateH(el), 0);
  const pfMM = (sections.pageFooter || []).reduce((s, el) => s + estimateH(el), 0);
  const bodyAvailMM = availMM - phMM - pfMM - 8 - 5;
  const rowMM = ((p.cellPadding || 8) * 2 + (p.fontSize || 11) + 2) / 3.78;
  const maxLines = Math.max(5, Math.floor(bodyAvailMM / rowMM));

  // Items to fill exactly `pageCount` pages + a small buffer to trigger the last break
  const needed = Math.ceil((maxLines * pageCount) / linesPerItem) + 2;
  return Math.min(needed, 120); // cap at 120 items
}

export function generatePreview(sections, pageSettings, data, targetPages = null) {
  const ps = { ...{ marginTop: 20, marginRight: 20, marginBottom: 20, marginLeft: 20, fontFamily: "Arial, sans-serif", fontSize: 12 }, ...pageSettings };

  const renderSection = (sectionKey, overrideData) =>
    (sections[sectionKey] || [])
      .filter((el) => el.type !== "broughtForward" && el.type !== "carriedForward")
      .map((el) => elementToHTML(el, overrideData || data))
      .join("\n");

  const lineItemsEl = (sections.body || []).find((el) => el.type === "lineItems");

  const css = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: ${ps.fontFamily}; font-size: ${ps.fontSize}px; color: #000000; line-height: 1.4; }
    table { border-collapse: collapse; width: 100%; }
    img { max-width: 100%; }
    .page-break-visual { height: 0; }
    /* Layout-primitive anti-fracture rules (mirror ejsGenerator CSS) */
    .two-columns-wrapper, .three-columns-wrapper, .four-columns-wrapper,
    .text-block-container, .layout-container-row,
    [data-component-type="two_columns"],
    [data-component-type="three_columns"],
    [data-component-type="four_columns"],
    [data-component-type="text_block"] {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    /* Page chrome — each preview page is a flex column so the bottom zone
       (page-footer / report-footer) is anchored against the bottom edge. */
    .preview-page-wrap {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      width: 100%;
      box-sizing: border-box;
    }
    .preview-zone-top, .preview-zone-bottom { flex: 0 0 auto; width: 100%; }
    .preview-zone-body { flex: 1 1 auto; width: 100%; }
  `;

  // ── Decide how many sheets to render ─────────────────────────────────
  // Always honour `targetPages` when provided. When lineItems is present we
  // ALSO respect the wrap-math result and take the max — that way a long
  // descriptions array can produce extra pages beyond the user's selection
  // (overflow-friendly per spec Task 3 edge case).
  let totalPages;
  let pageItemSlices; // items per page; empty arrays when no lineItems
  if (lineItemsEl) {
    const pageStarts = computePageBreaks(lineItemsEl, data, ps, sections);
    const mathPages = Math.max(1, pageStarts.length - 1);
    totalPages = Math.max(mathPages, targetPages || 1);
    const allItems = data[lineItemsEl.props.loopVariable || "items"] || [];
    pageItemSlices = [];
    for (let i = 0; i < totalPages; i++) {
      const s = pageStarts[i] != null ? pageStarts[i] : allItems.length;
      const e = pageStarts[i + 1] != null ? pageStarts[i + 1] : allItems.length;
      pageItemSlices.push(allItems.slice(s, e));
    }
  } else {
    totalPages = Math.max(1, targetPages || 1);
    pageItemSlices = Array.from({ length: totalPages }, () => []);
  }

  // Body partitioning — elements before/after the lineItems anchor.
  const bodyEls = sections.body || [];
  const liIdx = lineItemsEl ? bodyEls.indexOf(lineItemsEl) : -1;
  const beforeEls = liIdx >= 0
    ? bodyEls.slice(0, liIdx).filter((el) => el.type !== "broughtForward")
    : bodyEls.filter((el) => el.type !== "broughtForward" && el.type !== "carriedForward");
  const afterEls = liIdx >= 0
    ? bodyEls.slice(liIdx + 1).filter((el) => el.type !== "carriedForward")
    : [];
  const cfEl = bodyEls.find((el) => el.type === "carriedForward");
  const bfEl = bodyEls.find((el) => el.type === "broughtForward");

  // ── Strict lifecycle per spec ────────────────────────────────────────
  //
  //   1-page total (Case A):
  //     [Report Header] → [Body] → [Report Footer]   (no Page Header/Footer)
  //
  //   Multi-page total (Case B):
  //     First page:   [Report Header] → [Body] → [Page Footer]    (no Report Footer)
  //     Middle pages: [Page Header]   → [Body] → [Page Footer]    (no Report Header/Footer)
  //     Last page:    [Page Header]   → [Body] → [Report Footer]  (no Report Header/Page Footer)
  const pages = [];
  for (let pg = 0; pg < totalPages; pg++) {
    const isFirst = pg === 0;
    const isLast = pg === totalPages - 1;
    const isSingle = totalPages === 1; // Case A
    const pageData = { ...data, currentPage: pg + 1, totalPages };

    // ── TOP zone ──────────────────────────────────────────────────────
    const topParts = [];
    if (isFirst) {
      // Case A: ReportHeader. Case B first page: ReportHeader. (Same.)
      const rhHtml = renderSection("reportHeader", pageData);
      if (rhHtml) topParts.push(wrapSection("reportHeader", rhHtml, ps));
    } else {
      // Middle + Last page (Case B): PageHeader.
      const phHtml = renderSection("pageHeader", pageData);
      if (phHtml) topParts.push(wrapSection("pageHeader", phHtml, ps));
      if (bfEl) topParts.push(elementToHTML(bfEl, pageData));
    }

    // ── BODY zone ─────────────────────────────────────────────────────
    const bodyParts = [];
    if (isFirst) {
      const beforeHtml = beforeEls.map((el) => elementToHTML(el, pageData)).join("\n");
      if (beforeHtml) bodyParts.push(wrapSection("body", beforeHtml, ps));
    }
    if (lineItemsEl) {
      bodyParts.push(renderLineItemsSlice(lineItemsEl.props, pageItemSlices[pg] || []));
      if (!isLast && cfEl) bodyParts.push(elementToHTML(cfEl, pageData));
    }
    if (isLast) {
      const afterHtml = afterEls.map((el) => elementToHTML(el, pageData)).join("\n");
      if (afterHtml) bodyParts.push(wrapSection("body", afterHtml, ps));
    }

    // ── BOTTOM zone ───────────────────────────────────────────────────
    const bottomParts = [];
    if (isSingle) {
      // Case A: ReportFooter ONLY (no PageFooter).
      const rfHtml = renderSection("reportFooter", pageData);
      if (rfHtml) bottomParts.push(wrapSection("reportFooter", rfHtml, ps));
    } else if (isLast) {
      // Case B last page: ReportFooter ONLY.
      const rfHtml = renderSection("reportFooter", pageData);
      if (rfHtml) bottomParts.push(wrapSection("reportFooter", rfHtml, ps));
    } else {
      // Case B first + middle pages: PageFooter (anchored bottom).
      const pfHtml = renderSection("pageFooter", pageData);
      if (pfHtml) bottomParts.push(wrapSection("pageFooter", pfHtml, ps));
    }

    pages.push(
      `<div class="preview-page-wrap">
  <div class="preview-zone-top">${topParts.filter(Boolean).join("\n")}</div>
  <div class="preview-zone-body">${bodyParts.filter(Boolean).join("\n")}</div>
  <div class="preview-zone-bottom">${bottomParts.filter(Boolean).join("\n")}</div>
</div>`
    );
  }

  return { pages, css };
}

/**
 * Build mock invoice data for preview simulations. Overlays variable-length
 * description strings on the configured `paginationWrapKey` column so the
 * pagination engine's wrap-aware line-counting math gets exercised properly.
 *
 * Strings are deterministic-ish (rotation through a fixed length pattern)
 * so successive previews of the same template don't flicker.
 */
export function generateMockInvoiceData(sections, pageSettings, targetPages) {
  const itemCount = computeItemsForPages(sections, pageSettings, targetPages);
  const base = generateSampleData(itemCount);

  // Identify the wrap key the same way ejsGenerator does: user-specified
  // paginationWrapKey, or auto-detect a 'description'-keyed column/subRow.
  const lineItemsEl = (sections.body || []).find((el) => el.type === "lineItems");
  if (!lineItemsEl) return base;

  const p = lineItemsEl.props;
  const userWrapKey = String(p.paginationWrapKey || "").trim();
  const wrapKey = userWrapKey
    || ((p.columns || []).find((c) => c.key === "description") ? "description" : null)
    || ((p.subRows || []).find((s) => s.key === "description") ? "description" : null);
  if (!wrapKey) return base;

  // Varied lengths chosen to test wrap math (30 → 100 chars range).
  const lengthPattern = [30, 49, 31, 56, 72, 38, 94, 41, 65, 88, 100, 53];
  const lorem = "Premium grade industrial component with extended life cycle and ISO certification ";

  const items = base[p.loopVariable || "items"] || [];
  const mocked = items.map((it, i) => {
    const len = lengthPattern[i % lengthPattern.length];
    let str = "";
    while (str.length < len) str += lorem;
    return { ...it, [wrapKey]: str.slice(0, len).trim() };
  });

  return { ...base, [p.loopVariable || "items"]: mocked };
}
